/** Byte staging contract. SEALED is not catalog validation or approval. */
export interface ChecklistCatalogDeliveryLimits {
  maxTransportBytes: number;
  maxRawBytes: number;
  maxProducts: number;
  maxTotalTransportBytes: number;
  maxTotalRawBytes: number;
  uploadUrlSeconds: number;
  stagingSeconds: number;
  retentionSeconds: number;
}
export interface ChecklistCatalogDeliveryOwner {
  kind: 'operator' | 'machine';
  /** Trusted authenticated principal; never read from the request body. */
  id: string;
}
export interface ChecklistCatalogDeliveryBaseline {
  snapshotFingerprint: string;
  sourceBinding: string;
  checklistTableId: string;
  catalogRevision: string;
  correctionRevision: string;
  retainedStateDigest: string;
  savedReferenceDigest: string;
  policyVersion: string;
}
export interface ChecklistCatalogDeliveryProduct {
  sourceTargetId: string;
  file: string;
  mode: 'ordinary' | 'replace';
  contentEncoding: 'identity' | 'gzip';
  transportSha256: string;
  transportBytes: number;
  rawSha256: string;
  rawBytes: number;
  expected: {
    rosterCount: number;
    parallelDefinitionCount: number;
    subsetDefinitionCount: number;
    snapshotFingerprint: string;
  };
  basedOn: ChecklistCatalogDeliveryBaseline;
  changeKeys: string[];
}
export interface ChecklistCatalogDeliveryManifest {
  schemaVersion: 1;
  deliveryId: string;
  sourceSystem: 'azure-sql-ebay';
  createdAt: string;
  products: ChecklistCatalogDeliveryProduct[];
}
export interface ChecklistCatalogDeliveryValidationError {
  path: string;
  code: string;
}
export function validateChecklistCatalogDeliveryLimits(
  limits: ChecklistCatalogDeliveryLimits,
): void {
  for (const key of [
    'maxTransportBytes',
    'maxRawBytes',
    'maxProducts',
    'maxTotalTransportBytes',
    'maxTotalRawBytes',
    'uploadUrlSeconds',
    'stagingSeconds',
    'retentionSeconds',
  ] as const) {
    const value = limits[key];
    if (!Number.isSafeInteger(value) || value <= 0) throw new Error('Invalid staging limits');
  }
  if (
    limits.maxTransportBytes > 20 * 1024 * 1024 ||
    limits.uploadUrlSeconds > 900 ||
    limits.uploadUrlSeconds > limits.stagingSeconds ||
    limits.retentionSeconds < limits.stagingSeconds ||
    limits.maxProducts > 10
  )
    throw new Error('Invalid staging limits');
}
const hex256 = /^[a-f0-9]{64}$/;
const hex64 = /^[a-f0-9]{16}$/;
const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max = 128): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= max && value.trim() === value;
const count = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
/** Object keys use UTF-16 lexical order, not locale order. Array order is significant. */
export function checklistCatalogDeliveryCanonicalJson(value: unknown): string {
  if (Array.isArray(value))
    return `[${value.map(checklistCatalogDeliveryCanonicalJson).join(',')}]`;
  if (object(value))
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${checklistCatalogDeliveryCanonicalJson(value[key])}`)
      .join(',')}}`;
  const encoded = JSON.stringify(value);
  if (encoded === undefined) throw new Error('Non-JSON manifest value');
  return encoded;
}
export function validateChecklistCatalogDeliveryManifest(
  value: unknown,
  limits: ChecklistCatalogDeliveryLimits,
):
  | { ok: true; manifest: ChecklistCatalogDeliveryManifest }
  | { ok: false; errors: ChecklistCatalogDeliveryValidationError[] } {
  validateChecklistCatalogDeliveryLimits(limits);
  const errors: ChecklistCatalogDeliveryValidationError[] = [];
  const fail = (path: string, code = 'invalid_field'): void => {
    errors.push({ path, code });
  };
  const fields = (v: Record<string, unknown>, allowed: string[], path: string): void => {
    for (const key of Object.keys(v))
      if (!allowed.includes(key)) fail(`${path}.${key}`, 'unknown_field');
  };
  if (!object(value)) return { ok: false, errors: [{ path: '$', code: 'invalid_object' }] };
  fields(value, ['schemaVersion', 'deliveryId', 'sourceSystem', 'createdAt', 'products'], '$');
  if (value.schemaVersion !== 1) fail('$.schemaVersion');
  if (
    typeof value.deliveryId !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      value.deliveryId,
    )
  )
    fail('$.deliveryId');
  if (value.sourceSystem !== 'azure-sql-ebay') fail('$.sourceSystem');
  if (
    !text(value.createdAt, 32) ||
    !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{3})?Z$/.test(value.createdAt) ||
    !Number.isFinite(Date.parse(value.createdAt))
  )
    fail('$.createdAt');
  if (
    !Array.isArray(value.products) ||
    value.products.length < 1 ||
    value.products.length > limits.maxProducts
  ) {
    fail('$.products', 'product_limit');
    return { ok: false, errors };
  }
  const targets = new Set<string>();
  const files = new Set<string>();
  let transportTotal = 0;
  let rawTotal = 0;
  value.products.forEach((p: unknown, index: number) => {
    const path = `$.products[${index}]`;
    if (!object(p)) {
      fail(path, 'invalid_object');
      return;
    }
    fields(
      p,
      [
        'sourceTargetId',
        'file',
        'mode',
        'contentEncoding',
        'transportSha256',
        'transportBytes',
        'rawSha256',
        'rawBytes',
        'expected',
        'basedOn',
        'changeKeys',
      ],
      path,
    );
    if (!text(p.sourceTargetId, 32) || !/^[1-9][0-9]*$/.test(p.sourceTargetId))
      fail(`${path}.sourceTargetId`);
    else if (targets.has(p.sourceTargetId)) fail(`${path}.sourceTargetId`, 'duplicate_target');
    else targets.add(p.sourceTargetId);
    if (
      !text(p.file, 160) ||
      !/^.+\.json(?:\.gz)?$/.test(p.file) ||
      /[/\\]/.test(p.file) ||
      Array.from(p.file).some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127) ||
      p.file.includes('..')
    )
      fail(`${path}.file`, 'invalid_filename');
    else if (files.has(p.file.normalize('NFC').toLocaleLowerCase('en-US')))
      fail(`${path}.file`, 'duplicate_file');
    else files.add(p.file.normalize('NFC').toLocaleLowerCase('en-US'));
    if (p.mode !== 'ordinary' && p.mode !== 'replace') fail(`${path}.mode`);
    if (p.contentEncoding !== 'identity' && p.contentEncoding !== 'gzip')
      fail(`${path}.contentEncoding`);
    if (typeof p.file === 'string' && (p.contentEncoding === 'gzip') !== p.file.endsWith('.gz'))
      fail(`${path}.file`, 'encoding_mismatch');
    for (const key of ['transportSha256', 'rawSha256'])
      if (typeof p[key] !== 'string' || !hex256.test(p[key] as string)) fail(`${path}.${key}`);
    for (const [key, maximum] of [
      ['transportBytes', limits.maxTransportBytes],
      ['rawBytes', limits.maxRawBytes],
    ] as const)
      if (!count(p[key]) || Number(p[key]) < 1 || Number(p[key]) > maximum)
        fail(`${path}.${key}`, 'byte_limit');
    if (count(p.transportBytes)) transportTotal += p.transportBytes;
    if (count(p.rawBytes)) rawTotal += p.rawBytes;
    if (
      p.contentEncoding === 'identity' &&
      (p.rawBytes !== p.transportBytes || p.rawSha256 !== p.transportSha256)
    )
      fail(path, 'identity_mismatch');
    if (!object(p.expected)) fail(`${path}.expected`, 'invalid_object');
    else {
      fields(
        p.expected,
        ['rosterCount', 'parallelDefinitionCount', 'subsetDefinitionCount', 'snapshotFingerprint'],
        `${path}.expected`,
      );
      for (const key of ['rosterCount', 'parallelDefinitionCount', 'subsetDefinitionCount'])
        if (!count(p.expected[key])) fail(`${path}.expected.${key}`);
      if (
        typeof p.expected.snapshotFingerprint !== 'string' ||
        !hex64.test(p.expected.snapshotFingerprint)
      )
        fail(`${path}.expected.snapshotFingerprint`);
    }
    if (!object(p.basedOn)) fail(`${path}.basedOn`, 'invalid_object');
    else {
      fields(
        p.basedOn,
        [
          'snapshotFingerprint',
          'sourceBinding',
          'checklistTableId',
          'catalogRevision',
          'correctionRevision',
          'retainedStateDigest',
          'savedReferenceDigest',
          'policyVersion',
        ],
        `${path}.basedOn`,
      );
      for (const key of [
        'sourceBinding',
        'checklistTableId',
        'catalogRevision',
        'correctionRevision',
        'policyVersion',
      ])
        if (!text(p.basedOn[key])) fail(`${path}.basedOn.${key}`);
      for (const key of ['retainedStateDigest', 'savedReferenceDigest'])
        if (typeof p.basedOn[key] !== 'string' || !hex256.test(p.basedOn[key] as string))
          fail(`${path}.basedOn.${key}`);
      if (
        typeof p.basedOn.snapshotFingerprint !== 'string' ||
        !hex64.test(p.basedOn.snapshotFingerprint)
      )
        fail(`${path}.basedOn.snapshotFingerprint`);
    }
    if (
      !Array.isArray(p.changeKeys) ||
      p.changeKeys.length > 100 ||
      p.changeKeys.some((k) => !text(k)) ||
      new Set(p.changeKeys).size !== p.changeKeys.length
    )
      fail(`${path}.changeKeys`);
  });
  if (!Number.isSafeInteger(transportTotal) || transportTotal > limits.maxTotalTransportBytes)
    fail('$.products', 'transport_total_limit');
  if (!Number.isSafeInteger(rawTotal) || rawTotal > limits.maxTotalRawBytes)
    fail('$.products', 'raw_total_limit');
  return errors.length
    ? { ok: false, errors }
    : { ok: true, manifest: value as unknown as ChecklistCatalogDeliveryManifest };
}

export interface CatalogDeliverySignedUpload {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
}

export interface CatalogDeliveryDto {
  deliveryId: string;
  manifestDigest: string;
  registeredAt: string;
  expiresAt: string;
  status: 'UPLOADING' | 'SEALED';
  catalogValidation: 'NOT_RUN';
  products: Array<{
    sourceTargetId: string;
    file: string;
    contentEncoding: 'identity' | 'gzip';
    transportBytes: number;
    transportSha256: string;
    rawBytes: number;
    rawSha256: string;
    status: 'UPLOADING' | 'SEALED';
    sealedAt?: string;
    downloadUrl?: string;
    downloadExpiresAt?: string;
  }>;
}
export interface CatalogDeliveryPrepareDto extends CatalogDeliveryDto {
  uploads: Array<CatalogDeliverySignedUpload & { sourceTargetId: string; expiresAt: string }>;
}
